import { useState } from 'react';
import { weekRange, dateAfter } from '../lib/oura';

/**
 * Freshness line for pages whose AI answers are re-checked weekly. Refresh is
 * an underlined text button per the house style for inline text actions.
 *
 * This used to render inside the blue "Oura Insights" banner. That banner has
 * been withdrawn, but the banner was only ever the container here: the content
 * is a real capture date, the sync cadence, the next scheduled check and a
 * working Refresh control. So the frame became neutral rather than the whole
 * component disappearing with the branding it happened to sit in.
 */
export function ScanFreshness({ capturedAt }: { capturedAt: string }) {
  const [state, setState] = useState<'idle' | 'refreshing' | 'done'>('idle');

  const refresh = () => {
    setState('refreshing');
    // Prototype: a real refresh would re-probe the engines and re-base the cycle.
    setTimeout(() => setState('done'), 1300);
  };

  // A run with no capture date cannot say when it was checked or when the next
  // check is due. Both clauses drop rather than printing a date derived from
  // the Unix epoch, which is what "checked 29 Dec to 4 Jan 1970" was.
  const hasDate = !!capturedAt;
  const nextCheck = state === 'done' ? dateAfter(capturedAt, 7) : dateAfter(capturedAt, 6);

  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-x-4 gap-y-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
      <div className="min-w-0 flex-1 text-sm text-gray-700">
      <span className="font-medium text-gray-900">
        {state === 'done'
          ? 'AI answers checked just now'
          : hasDate
            ? `AI answers checked ${weekRange(capturedAt)}`
            : 'AI answers not checked yet'}
      </span>
      <span className="text-gray-400"> · </span>
      Google spots and reviews sync daily
      {hasDate && (
        <>
          <span className="text-gray-400"> · </span>
          Next automatic check {nextCheck}
        </>
      )}
      </div>
      <div className="flex-shrink-0 pt-0.5">
        <button
          onClick={refresh}
          disabled={state === 'refreshing'}
          className="text-sm font-medium text-blue-600 underline-offset-4 hover:underline disabled:pointer-events-none disabled:opacity-60"
        >
          {state === 'refreshing' ? 'Refreshing…' : state === 'done' ? 'Refresh again' : 'Refresh now'}
        </button>
      </div>
    </div>
  );
}
